// cloudview: wiring of viewer, controls, panel, tools and URL state.
import { Viewer, hex2rgb, type Cloud } from './viewer.ts';
import * as cache from './cache.ts';
import { clearFeats } from './photo/featstore.ts';
import { Prefetcher } from './prefetch.ts';
import { proc, type Proc } from './procs.ts';
import type { OrigEntry } from './format.ts';
import { Controls, followPane } from './controls.ts';
import { type V3, dist, project, niceLength, cross, norm, dot, scale, sub, add } from './math.ts';
import { parseHash, formatHash, nums, fmt, type HashState } from './hash.ts';
import type { Mode } from './camera.ts';
import { VolumeEditor } from './volumeui.ts';
import { AlignTool } from './aligntool.ts';
import { EditUI } from './edit/editui.ts';
import { sceneParam, listScenes, renderSelector, publicPicker } from './scenes.ts';
import { editorFeedback } from './edit/feedback.ts';
import { Dock } from './dock/dock.ts';
import { VIEWPORT, group, split } from './dock/tree.ts';
import { FloatWin } from './dock/float.ts';
import { initStatusBar } from './statusbar.ts';
import { initSettings } from './settings.ts';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const inp = (id: string) => $<HTMLInputElement>(id);
const el = (tag: string, attrs: Record<string, string> = {}, text = '') => {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  if (text) e.textContent = text;
  return e;
};
const fail = (m: string) => { const e = $('err'); e.textContent = m; e.hidden = false; };

// the layout: tabs in groups (src/dock); the editor's tabs join it once the editor answers (EditUI.onEditor).
// One layout for the viewer and the editor: in the viewer the editor's tabs and the groups of only those don't show.
const dock = new Dock($('dock'), $('panes'), 'index', split('row', [
  group(['selection', 'shapes', 'points', 'layers']),
  split('col', [group([VIEWPORT, 'construction']), group(['history', 'macros', 'macroedit', 'nodes'])], [0.75, 0.25]),
  split('col', [group(['classes', 'objects', 'groups', 'data', 'scans']), group(['display', 'edits', 'volumes', 'overlays', 'measure', 'perf', 'settings'])], [0.45, 0.55]),
], [0.22, 0.54, 0.24]));
dock.registerFrom($('panes'), false);
dock.render();
dock.menu($('winbtn'));
initStatusBar();

// Options: the Display tab, lent by the layout, as a popup under its button (back in its tab when it closes)
const optsPop = el('div', { id: 'optspop' });
optsPop.hidden = true;
document.body.append(optsPop);
function showOpts(open: boolean) {
  if (open === !optsPop.hidden) return;
  $('optsbtn').classList.toggle('on', open);
  if (!open) { optsPop.hidden = true; dock.unlend('display'); return; }
  const p = dock.lend('display', () => showOpts(false));
  if (!p) return;
  optsPop.replaceChildren(p);
  optsPop.hidden = false;
  const r = $('optsbtn').getBoundingClientRect();
  optsPop.style.top = `${r.bottom + 6}px`;
  optsPop.style.maxHeight = `${innerHeight - r.bottom - 14}px`;
  optsPop.style.left = `${Math.max(8, Math.min(r.left, innerWidth - optsPop.offsetWidth - 8))}px`;
}
$('optsbtn').onclick = () => showOpts(optsPop.hidden);
addEventListener('pointerdown', e => { if (!optsPop.hidden && !optsPop.contains(e.target as Node) && !$('optsbtn').contains(e.target as Node)) showOpts(false); }, true);
addEventListener('keydown', e => { if (!optsPop.hidden && e.key === 'Escape') { showOpts(false); e.stopPropagation(); } }, true);

// panels opened in a movable window rather than their tab (the macro editor, from a saved macro's ✎), one window each
const wins = new Map<string, FloatWin>();
function popOut(id: string, title: string) {
  let w = wins.get(id);
  if (!w) { w = new FloatWin(id, title, { width: 460, onClose: () => dock.unlend(id) }); wins.set(id, w); }
  const win = w;
  win.setTitle(title);
  if (!win.isOpen) {
    const p = dock.lend(id, () => win.close());
    if (!p) return;
    win.body.replaceChildren(p);
  }
  win.open();
}
/** A panel to the front: its window if it is in one, else its tab. */
function showPanel(id: string) {
  const w = wins.get(id);
  if (w?.isOpen) w.front(); else dock.show(id);
}

const canvas = $<HTMLCanvasElement>('c');
let v: Viewer;
try { v = new Viewer(canvas); } catch (e) { fail(String((e as Error).message || e)); throw e; }
const ctl = new Controls(v, canvas);
ctl.strokeFrom = t => !!t.closest('#viewport, #construction');   // a lasso may start over the see-through tab(s) over the view, their cards too
initSettings(ctl);
new ResizeObserver(() => { v.dirty = true; }).observe(canvas);   // its pane resized
const cam = v.cam, S = v.s;
dock.onChange = followPane(v, canvas, $('viewport'), $('construction'));   // the view's centre: the View tab's middle (or Construction's, beside it)
const touch = matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 1;
S.budget = touch ? 1.5e6 : 3e6;
let northUp = true;
const measure: V3[] = [];
let measuring = false;
const ovOn = new Set<string>(['outline', 'north', 'scale']);
let hashTimer = 0;

// the scene (?scene=…): its data is data/<scene>/ and, in the editor, its API api/scenes/<scene>/.
// Without one the editor shows its scenes; the public site shows the church. ?data= still names a data dir.
const scene = sceneParam();
const legacyData = (() => {
  const q = new URLSearchParams(location.search).get('data');
  return q && /^[\w\-./]+\/$/.test(q) && !q.includes('..') ? q : null;
})();
const dataBase = legacyData ?? `data/${scene ?? 'church'}/`;

const vols = new VolumeEditor(v, canvas, dataBase, $);
const align = new AlignTool(v, dataBase, $);
const edit = new EditUI(v, ctl, dataBase, $, () => !!vols.sel, legacyData ? null : scene ?? 'church');
align.onSave = moved => edit.saveAlignment(moved);
align.pointsOf = id => edit.alignPoints(id);

// ── modes ─────────────────────────────────────────────────────────────
function setMode(m: Mode) {
  const was = cam.mode;
  if (m === was) return;
  if (m === 'plan') {
    const p = ctl.pivot ?? cam.eye;
    cam.pc = [p[0], p[1]];
  } else if (was === 'plan') {
    cam.eye = [cam.pc[0] - Math.cos(cam.yaw) * 8, cam.pc[1] - Math.sin(cam.yaw) * 8, v.floorZ + 6];
  }
  if (m === 'walk') { cam.eye = [cam.eye[0], cam.eye[1], v.floorZ + 1.6]; cam.pitch = 0; }
  if (m !== 'walk' && document.pointerLockElement) document.exitPointerLock();
  cam.mode = m;
  syncModeUI();
  ctl.touched();
}
function syncModeUI() {
  for (const b of $('modes').querySelectorAll('button')) b.classList.toggle('on', b.dataset.m === cam.mode);
  $('planup').hidden = cam.mode !== 'plan';
  $('planup').textContent = northUp ? 'North up' : 'Page up';
  $('joy').hidden = !(touch && cam.mode === 'walk');
  setPlanUp();
}
function setPlanUp() {
  const n = Object.values(v.scene?.frames ?? {})[0]?.north;
  cam.up2 = northUp && n ? [n[0], n[1]] : [0, 1];
}
$('modes').addEventListener('click', e => { const m = (e.target as HTMLElement).dataset.m; if (m) setMode(m as Mode); });
$('planup').onclick = () => { northUp = !northUp; syncModeUI(); ctl.touched(); };

// ── panel ─────────────────────────────────────────────────────────────
const bindRange = (id: string, f: (x: number) => void) => { const i = inp(id); i.oninput = () => { f(+i.value); syncOutputs(); dirty(); }; };
const dirty = () => { v.dirty = true; queueHash(); };
inp('shade').onchange = () => { S.mode = +inp('shade').value; syncPanel(); dirty(); };
// the same choice on the main view: an icon button per mode (the name in its tooltip)
const SHADE_ICONS = [
  '<circle cx="7" cy="7.5" r="4.5" fill="#f44" fill-opacity=".85"/><circle cx="13" cy="7.5" r="4.5" fill="#4d4" fill-opacity=".85"/><circle cx="10" cy="12.5" r="4.5" fill="#48f" fill-opacity=".85"/>',
  '<defs><radialGradient id="shlit" cx=".35" cy=".35" r=".75"><stop offset="0" stop-color="#fff"/><stop offset="1" stop-color="#333"/></radialGradient></defs><circle cx="10" cy="10" r="7.5" fill="url(#shlit)"/>',
  '<defs><linearGradient id="shh" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stop-color="#301040"/><stop offset=".25" stop-color="#1c87f0"/><stop offset=".5" stop-color="#4cf078"/><stop offset=".75" stop-color="#fabb26"/><stop offset="1" stop-color="#ba1408"/></linearGradient></defs><path d="M2 18 7 6l4 7 3-5 4 10Z" fill="url(#shh)"/>',
  '<circle cx="6.5" cy="7" r="4" fill="#e8a33a"/><circle cx="13.5" cy="7" r="4" fill="#6ab0f0"/><circle cx="10" cy="13.5" r="4" fill="#b07ae0"/>',
  '<path d="M3 16h14" stroke="#9aa0a6" stroke-width="2"/><path d="M10 15V5M6.5 8 10 4.5 13.5 8" stroke="#8c8cf8" stroke-width="2" fill="none"/><path d="M4 14 2.5 10M16 14l1.5-4" stroke="#f08ca0" stroke-width="1.6"/>',
  '<path d="M2 16a8 8 0 0 1 16 0" stroke="#f44" stroke-width="2" fill="none"/><path d="M4.5 16a5.5 5.5 0 0 1 11 0" stroke="#fd3" stroke-width="2" fill="none"/><path d="M7 16a3 3 0 0 1 6 0" stroke="#4d4" stroke-width="2" fill="none"/><path d="M9 16a1 1 0 0 1 2 0" stroke="#48f" stroke-width="2" fill="none"/>',
  '<rect x="1.5" y="8" width="5" height="4" rx="1" fill="#9aa0a6"/><path d="M6.5 10l1.5-1.5v3Z" fill="#9aa0a6"/><path d="M10 5.5a6 6 0 0 1 0 9" stroke="#1c87f0" stroke-width="1.8" fill="none"/><path d="M13 3.5a9 9 0 0 1 0 13" stroke="#fabb26" stroke-width="1.8" fill="none"/><path d="M16 1.5a12 12 0 0 1 0 17" stroke="#ba1408" stroke-width="1.8" fill="none"/>',
  '<rect x="2" y="2" width="16" height="16" rx="2" fill="#26282c"/><path d="M4 15 8.5 6l3 5 2-3L16 15" stroke="#fd3" stroke-width="1.6" fill="none" stroke-linejoin="round"/>',
  '<circle cx="4" cy="15" r="1.3" fill="#1c87f0"/><circle cx="8" cy="5" r="1.3" fill="#1c87f0"/><circle cx="16" cy="16" r="1.3" fill="#4cf078"/><circle cx="12" cy="10" r="1.3" fill="#fabb26"/><circle cx="14.5" cy="8" r="1.3" fill="#ba1408"/><circle cx="12" cy="7" r="1.3" fill="#ba1408"/><circle cx="15" cy="11.5" r="1.3" fill="#ba1408"/><circle cx="10" cy="12.5" r="1.3" fill="#fabb26"/>',
  '<rect x="1.5" y="3" width="17" height="14" rx="1.5" fill="#26282c"/><path d="M3.5 14.5 16.5 5.5" stroke="#ff3b30" stroke-width="1.6"/><path d="M3.5 6.5h6" stroke="#00e6ff" stroke-width="1.6"/><circle cx="13.5" cy="12.5" r="2.2" stroke="#30d158" stroke-width="1.5" fill="none"/>',
  '<rect x="1.5" y="2.5" width="11" height="9" rx="1" fill="#e8a33a" fill-opacity=".9"/><rect x="5" y="6" width="11" height="9" rx="1" fill="#48f" fill-opacity=".6"/><rect x="7.5" y="9" width="11" height="9" rx="1" fill="#4d4" fill-opacity=".35"/>',
  '<text x="10" y="8.5" text-anchor="middle" dominant-baseline="middle" font-size="15" font-weight="700" fill="#fabb26">#</text><path d="M2 17h4" stroke="#1c87f0" stroke-width="2.5"/><path d="M6 17h4" stroke="#4cf078" stroke-width="2.5"/><path d="M10 17h4" stroke="#fabb26" stroke-width="2.5"/><path d="M14 17h4" stroke="#ba1408" stroke-width="2.5"/>',
];
for (const o of ($('shade') as HTMLSelectElement).options) {
  const b = el('button', { class: 'small', title: o.textContent!, 'aria-label': o.textContent!, role: 'radio' });
  b.dataset.m = o.value;
  b.hidden = o.hidden;   // Point density, Image features, Image weights and Data group: the editor's (unhidden when it is on)
  b.innerHTML = `<svg viewBox="0 0 20 20">${SHADE_ICONS[+o.value] ?? ''}</svg>`;
  $('shadetop').append(b);
}
$('shadetop').setAttribute('role', 'radiogroup');
$('shadetop').addEventListener('click', e => {
  const m = (e.target as HTMLElement).closest('button')?.dataset.m;
  if (m != null) { S.mode = +m; syncPanel(); dirty(); }
});
// selection only (the editor's): just the selected points, in whichever shading; everything while nothing is selected
inp('selonly').onchange = () => { S.selOnly = inp('selonly').checked; dirty(); };
inp('cull').onchange = () => { S.cull = inp('cull').checked; syncPanel(); dirty(); };
bindRange('cullT', x => S.cullT = x);
inp('period').onchange = () => { S.period = +inp('period').value; dirty(); };
const RANGE_MAXES = [2, 3, 4, 5];   // Capture range: the options of where the ramp turns red (m)
inp('rangemax').onchange = () => { S.rangeMax = +inp('rangemax').value; dirty(); };
inp('edgeview').onchange = () => { S.edgeView = +inp('edgeview').value; dirty(); };
inp('featview').onchange = () => { S.featView = +inp('featview').value; dirty(); };
inp('wphoto').onchange = () => { S.imgOp = inp('wphoto').value as typeof S.imgOp; syncPanel(); dirty(); };
inp('wexpo').onchange = () => { S.imgExpo = inp('wexpo').checked; syncPanel(); dirty(); };
bindRange('imglod', x => S.imgLod = 6 / x);
const DENS_RS = [0.02, 0.05, 0.1, 0.2];   // Point density: the radius options (m)
inp('densr').onchange = () => { S.densR = +inp('densr').value; syncPanel(); dirty(); };
bindRange('densK', x => S.densK = x);
bindRange('gain', x => S.gain = x);
bindRange('size', x => S.size = x);
bindRange('edls', x => S.edlStrength = x);
bindRange('edlr', x => S.edlRadius = x);
bindRange('fillr', x => S.fillR = x);
inp('fill').onchange = () => { S.fill = inp('fill').checked; dirty(); };
inp('voxels').onchange = () => { S.voxels = inp('voxels').checked; dirty(); };
bindRange('detail', x => S.targetPx = 3.2 / x);
inp('edl').onchange = () => { S.edl = inp('edl').checked; dirty(); };
inp('budget').onchange = () => { S.budget = +inp('budget').value; dirty(); };
inp('dpr').onchange = () => { S.dprCap = +inp('dpr').value; dirty(); };
function syncOutputs() {
  $('cullTv').textContent = Math.round(Math.acos(S.cullT) * 180 / Math.PI) + '°';   // the angle the threshold keeps
  $('densKv').textContent = Math.round(v.densRed()).toLocaleString();   // points within densR at the red end
}
function syncPanel() {
  inp('shade').value = String(S.mode); inp('period').value = String(S.period); inp('rangemax').value = String(S.rangeMax); inp('edgeview').value = String(S.edgeView); inp('featview').value = String(S.featView); inp('densr').value = String(S.densR);
  for (const b of $('shadetop').querySelectorAll('button')) {
    const on = b.dataset.m === String(S.mode);
    b.classList.toggle('on', on); b.setAttribute('aria-checked', String(on));
  }
  inp('selonly').checked = S.selOnly; inp('cull').checked = S.cull;
  inp('cullT').value = String(S.cullT); $('cullTl').classList.toggle('off', !S.cull);
  // the mode's own options, under it in the Options popup (gain scales the scan colours, so RGB only)
  const rgb = S.mode === 0;
  $('gainl').hidden = !rgb; $('periodl').hidden = S.mode !== 5; $('rangemaxl').hidden = S.mode !== 6; $('edgeviewl').hidden = S.mode !== 7;
  $('densrl').hidden = $('denslegend').hidden = $('densKl').hidden = S.mode !== 8;
  $('featviewl').hidden = $('featlegend').hidden = S.mode !== 9;
  $('wlegend').hidden = $('wphotol').hidden = $('wexpol').hidden = $('imglodl').hidden = S.mode !== 10;
  $('datal').hidden = S.mode !== 11;
  if (S.mode !== 11) { $('datacut').hidden = true; v.valCut = null; }
  inp('wphoto').value = S.imgOp; inp('wexpo').checked = S.imgExpo; inp('imglod').value = String(6 / S.imgLod);
  inp('wexpo').disabled = S.imgOp !== 'rgb' && S.imgOp !== 'grey';
  inp('densK').value = String(S.densK);
  $('shadeopts').hidden = !rgb && S.mode < 5;
  inp('gain').value = String(S.gain); inp('size').value = String(S.size);
  inp('fill').checked = S.fill; inp('fillr').value = String(S.fillR);
  inp('voxels').checked = S.voxels; $('voxelsl').hidden = !v.hasVoxels();
  inp('edl').checked = S.edl; inp('edls').value = String(S.edlStrength); inp('edlr').value = String(S.edlRadius);
  inp('budget').value = String(S.budget); inp('dpr').value = String(S.dprCap);
  inp('detail').value = String(3.2 / S.targetPx);
  syncOutputs();
  edit.shading();   // Point density and Image features: worked out by the editor when chosen
}

// Movable objects aren't listed as scans: they show with their scan, and by type in Objects.
const isObj = (c: Cloud) => c.meta.kind === 'object';
/** An object's type: its "of" (set in the object editor), else its name without the number ("bench 12": bench). */
const objType = (c: Cloud) => c.meta.of ?? (c.meta.name.replace(/\s+\d+$/, '') || c.meta.name);
const typeOff = new Set<string>();
/** Each object shows when its scan does and its type is ticked. */
function applyObjects() {
  const byId = new Map(v.clouds.map(c => [c.meta.id, c]));
  for (const c of v.clouds) if (isObj(c)) c.visible = !typeOff.has(objType(c)) && (byId.get(c.meta.parent!)?.visible ?? true);
}
/** The types to show unticked after reading the hash: those with none of their objects shown (among those whose scan is). */
function initTypes() {
  const byId = new Map(v.clouds.map(c => [c.meta.id, c])), seen = new Map<string, boolean>();
  for (const c of v.clouds) {
    if (!isObj(c) || byId.get(c.meta.parent!)?.visible === false) continue;
    const t = objType(c);
    seen.set(t, (seen.get(t) ?? false) || c.visible);
  }
  typeOff.clear();
  for (const [t, any] of seen) if (!any) typeOff.add(t);
  applyObjects();
}
const objChanged = () => { applyObjects(); buildCloudList(); edit.sync(); dirty(); };

function buildCloudList() {
  const box = $('clouds');
  box.textContent = '';
  const scans = v.clouds.filter(c => !isObj(c));
  for (const c of scans) {
    const row = el('div', { class: 'cloud', title: `${c.meta.notes}${c.meta.fit != null ? ` (fit ${c.meta.fit.toFixed(2)})` : ''}` });
    const on = el('input', { type: 'checkbox' }) as HTMLInputElement;
    on.checked = c.visible;
    on.onchange = () => { c.visible = on.checked; applyObjects(); edit.sync(); dirty(); };
    const col = el('input', { type: 'color', value: c.meta.color }) as HTMLInputElement;
    col.oninput = () => { c.color = hex2rgb(col.value); c.meta.color = col.value; if (S.mode !== 3) { S.mode = 3; syncPanel(); } dirty(); };
    const solo = el('button', { class: 'small', title: 'Solo' }, 'S');
    solo.onclick = () => {
      const only = scans.every(o => o.visible === (o === c));
      for (const o of scans) o.visible = only || o === c;
      objChanged();
    };
    const n = el('span', { class: 'n' });
    n.dataset.cloud = String(c.file);
    const mark = c.meta.kind === 'object' ? '◆ ' : c.meta.kind === 'part' ? '↳ ' : '';
    row.append(on, col, el('span', { class: 'name' }, mark + c.meta.name + (c.meta.fit != null && c.meta.fit < 1 ? ` · fit ${c.meta.fit.toFixed(2)}` : '')), n, solo);
    box.append(row);
  }
  const types = new Map<string, number>();
  for (const c of v.clouds) if (isObj(c)) types.set(objType(c), (types.get(objType(c)) ?? 0) + 1);
  const obox = $('objtypes');
  obox.textContent = '';
  for (const [t, k] of [...types].sort(([a], [b]) => a.localeCompare(b))) {
    const on = el('input', { type: 'checkbox' }) as HTMLInputElement;
    on.checked = !typeOff.has(t);
    on.onchange = () => { if (on.checked) typeOff.delete(t); else typeOff.add(t); objChanged(); };
    const row = el('div', { class: 'cloud', title: `${k} movable object${k === 1 ? '' : 's'}, each shown with its scan` });
    row.append(on, el('span'), el('span', { class: 'name' }, '◆ ' + t), el('span', { class: 'n' }, String(k)), el('span'));
    obox.append(row);
  }
  $('objsec').hidden = !types.size;
}
const showAll = (on: boolean) => { for (const c of v.clouds) if (!isObj(c)) c.visible = on; objChanged(); };
const showTypes = (on: boolean) => { typeOff.clear(); if (!on) for (const c of v.clouds) if (isObj(c)) typeOff.add(objType(c)); objChanged(); };
$('scansall').onclick = () => showAll(true);
$('scansnone').onclick = () => showAll(false);
$('objall').onclick = () => showTypes(true);
$('objnone').onclick = () => showTypes(false);

// twice: in the Overlays tab and in Display, both following ovOn
const OV_BOXES = ['overlays', 'doverlays'];
function buildOverlayList() {
  for (const b of OV_BOXES) $(b).textContent = '';
  const add = (id: string, label: string) => {
    for (const b of OV_BOXES) {
      const l = el('label');
      const i = el('input', { type: 'checkbox', 'data-ov': id }) as HTMLInputElement;
      i.checked = ovOn.has(id);
      i.onchange = () => { i.checked ? ovOn.add(id) : ovOn.delete(id); applyOverlays(); };
      l.append(i, document.createTextNode(' ' + label));
      $(b).append(l);
    }
  };
  for (const h of v.scene.overlays?.sectionHeights ?? []) add('sec' + h, `Section ${h.toFixed(1)} m`);
  if (v.lines.some(l => l.id === 'outline')) add('outline', 'OSM outline');
  if (v.lines.some(l => l.id.startsWith('cam:'))) add('cameras', 'Camera paths');
  add('grid', 'Grid (1 m / 5 m)');
  add('north', 'North arrow');
  add('scale', 'Scale bar');
}
function applyOverlays() {
  for (const b of OV_BOXES) for (const i of $(b).querySelectorAll<HTMLInputElement>('input[data-ov]')) i.checked = ovOn.has(i.dataset.ov!);
  for (const l of v.lines) {
    if (l.id === 'grid5') l.visible = ovOn.has('grid');
    else if (l.id.startsWith('cam:')) l.visible = ovOn.has('cameras');
    else if (l.id !== 'measure' && !l.id.startsWith('vol:')) l.visible = ovOn.has(l.id);
  }
  $('north').style.display = ovOn.has('north') && Object.values(v.scene.frames)[0]?.north ? '' : 'none';
  $('scalebar').style.display = ovOn.has('scale') ? '' : 'none';
  dirty();
}

// ── measuring ─────────────────────────────────────────────────────────
$('measure').onclick = () => { measuring = !measuring; $('measure').classList.toggle('on', measuring); if (measuring) vols.setPlacing(false); };
$('mundo').onclick = () => { measure.pop(); measureChanged(); };
$('mclear').onclick = () => { measure.length = 0; measureChanged(); };
ctl.onTap = (x, y) => {   // unused (false): the controls focus the point
  if (vols.place(x, y)) return true;
  if (edit.tap(x, y)) return true;
  if (!measuring) return false;
  const h = v.pickAt(x, y);
  if (h) { measure.push(h.p); measureChanged(); }
  return true;
};
ctl.onContext = (x, y) => edit.context(x, y);
addEventListener('keydown', e => { if (e.code === 'Escape' && measuring && measure.length) { measure.length = 0; measureChanged(); } });
const seg = (a: V3, b: V3) => ({ d: dist(a, b), h: Math.hypot(b[0] - a[0], b[1] - a[1]), z: b[2] - a[2] });
function measureChanged() {
  const e: number[] = [];
  for (let i = 1; i < measure.length; i++) e.push(...measure[i - 1], ...measure[i]);
  v.setLines('measure', new Float32Array(e), [1, 0.84, 0.04, 1], true, false);
  let t = 0, th = 0;
  const lines = measure.slice(1).map((b, i) => {
    const s = seg(measure[i], b); t += s.d; th += s.h;
    return `${i + 1}: ${s.d.toFixed(3)} m  (h ${s.h.toFixed(3)}, Δz ${s.z >= 0 ? '+' : ''}${s.z.toFixed(3)})`;
  });
  if (measure.length > 2) lines.push(`total ${t.toFixed(3)} m (h ${th.toFixed(3)})`);
  if (measure.length === 1) lines.push(`point ${fmt(measure[0], 3)}`);
  $('mout').textContent = lines.join('\n');
  v.dirty = true;
}
function drawLabels() {
  const box = $('labels');
  box.textContent = '';
  const scr = (p: V3) => { const q = project(cam.vp, p); return q[3] > 0 && Math.abs(q[2]) <= 1 ? [(q[0] + 1) / 2 * cam.w, (1 - q[1]) / 2 * cam.h] : null; };
  measure.forEach((p, i) => {
    const s = scr(p);
    if (s) { const d = el('div', { class: 'dot' }); d.style.left = s[0] + 'px'; d.style.top = s[1] + 'px'; box.append(d); }
    if (!i) return;
    const m = scr(scale(add(p, measure[i - 1]), 0.5) as V3);
    if (!m) return;
    const sg = seg(measure[i - 1], p);
    const l = el('div', { class: 'lbl' }, `${sg.d.toFixed(2)} m · h ${sg.h.toFixed(2)} · Δz ${sg.z.toFixed(2)}`);
    l.style.left = m[0] + 'px'; l.style.top = m[1] + 'px';
    box.append(l);
  });
}

// ── joystick (walk on touch) ──────────────────────────────────────────
{
  const j = $('joy'), k = $('knob');
  let id = -1;
  const set = (e: PointerEvent) => {
    const r = j.getBoundingClientRect();
    let x = (e.clientX - r.left - 60) / 50, y = (e.clientY - r.top - 60) / 50;
    const l = Math.hypot(x, y);
    if (l > 1) { x /= l; y /= l; }
    ctl.joy = [x, y];
    k.style.transform = `translate(${x * 40}px,${y * 40}px)`;
  };
  j.addEventListener('pointerdown', e => { id = e.pointerId; j.setPointerCapture(id); set(e); e.stopPropagation(); });
  j.addEventListener('pointermove', e => { if (e.pointerId === id) set(e); });
  const end = (e: PointerEvent) => { if (e.pointerId !== id) return; id = -1; ctl.joy = [0, 0]; k.style.transform = ''; };
  j.addEventListener('pointerup', end);
  j.addEventListener('pointercancel', end);
}

// ── HUD: north arrow, scale bar, stats ────────────────────────────────
function hud() {
  const n = Object.values(v.scene.frames)[0]?.north;
  if (n) {
    // angle of north on screen, clockwise from up
    const N: V3 = [n[0], n[1], 0];
    const r = cam.r, u = cam.ortho ? cam.u : norm(cross([0, 0, 1], r));
    const a = Math.atan2(dot(N, r), dot(N, u));
    $('north').style.transform = `rotate(${a}rad)`;
  }
  const d = cam.ortho ? 1 : Math.max(0.2, ctl.pivot ? dot(sub(ctl.pivot, cam.eye), cam.f) : ctl.depth);
  const mpp = d / cam.pxPerM;
  const L = niceLength(mpp * 140);
  $('sbar').style.width = L / mpp + 'px';
  $('slabel').textContent = (L >= 1 ? `${L} m` : `${Math.round(L * 100)} cm`) + (cam.ortho ? '' : ' at pivot');
}
let fps = 0, frames = 0, tFps = performance.now(), t0 = performance.now(), settled = 0;
function stats() {
  const total = v.clouds.reduce((s, c) => s + c.meta.points, 0);
  for (const s of $('clouds').querySelectorAll<HTMLElement>('.n')) {
    const c = v.clouds[+s.dataset.cloud!];
    const ld = c.chunks.reduce((s, ch) => s + ch.ends[ch.levels], 0);
    s.textContent = ld >= c.meta.points ? fmtN(c.meta.points) : `${fmtN(ld)}/${fmtN(c.meta.points)}`;
  }
  const pct = Math.round((100 * v.loadedPts) / Math.min(total, S.budget));
  $('status').textContent = `${fmtN(v.drawnPts)} pts · ${fps ? fps.toFixed(0) + ' fps' : 'idle'}${v.inFlight ? ` · loading ${Math.min(pct, 99)}%` : ''}`;
  copyProc();   // the editor's scans loading for editing, and other work, show in the status bar themselves (procs.ts)
  $('stats').textContent = `drawn ${fmtN(v.drawnPts)}${v.drawnImgTris ? ` (and ${fmtN(v.drawnImgTris)} photo triangles)` : ''}, loaded ${fmtN(v.loadedPts)} / ${fmtN(total)}\n` +
    `fetched ${(v.bytesIn / 1e6).toFixed(1)} MB, ${(v.bytesCached / 1e6).toFixed(1)} MB from the browser cache, in flight ${v.inFlight}${v.failures ? `, failures ${v.failures}` : ''}\n` +
    `render ${(v.renderScale() * cam.w) | 0}×${(v.renderScale() * cam.h) | 0} (dpr ${v.dpr})` +
    (settled ? `\nfirst view loaded in ${(settled / 1000).toFixed(1)} s` : '') + copyLine();
}
const fmtN = (n: number) => (n >= 1e6 ? (n / 1e6).toFixed(2) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(0) + 'k' : String(n));

// ── URL hash ──────────────────────────────────────────────────────────
function queueHash() { clearTimeout(hashTimer); hashTimer = setTimeout(writeHash, 400) as unknown as number; }
function writeHash() {
  const h: HashState = { m: cam.mode };
  if (cam.mode === 'plan') { h.p = fmt([cam.pc[0], cam.pc[1], cam.ps], 4); h.pu = northUp ? 'n' : 'p'; }
  else { h.e = fmt(cam.eye); h.a = fmt([cam.yaw, cam.pitch], 4); }
  if (ctl.pivot) h.pv = fmt(ctl.pivot);
  h.s = fmt([S.mode, S.gain, S.size, S.edl ? S.edlStrength : 0, S.edlRadius, S.fill ? 1 : 0, S.fillR, S.period, S.selOnly ? 1 : 0, S.rangeMax, S.cull ? 1 : 0, S.edgeView, S.cullT, S.densR, S.densK, S.voxels ? 1 : 0, S.featView], 2);
  const off = v.clouds.filter(c => !c.visible).map(c => c.file);
  if (off.length) h.off = off.join(',');
  h.ov = [...ovOn].join(',');
  history.replaceState(null, '', formatHash(h));
}
function readHash() {
  const h = parseHash(location.hash);
  let a: number[] | null;
  if ((a = nums(h.e, 3))) cam.eye = a as V3;
  if ((a = nums(h.a, 2))) { cam.yaw = a[0]; cam.pitch = a[1]; }
  if ((a = nums(h.p, 3))) { cam.pc = [a[0], a[1]]; cam.ps = a[2]; }
  if ((a = nums(h.pv, 3))) ctl.pivot = a as V3;
  if (h.pu) northUp = h.pu !== 'p';
  if ((a = nums(h.s)) && a.length >= 5) {
    [S.mode, S.gain, S.size] = a; S.edl = a[3] > 0; if (a[3] > 0) S.edlStrength = a[3]; S.edlRadius = a[4];
    if (a.length >= 7) { S.fill = a[5] > 0; S.fillR = a[6]; }
    if (a.length >= 8 && [4, 8, 16, 32, 64].includes(a[7])) S.period = a[7];
    S.selOnly = a[8] > 0;
    if (S.mode === 6 && a.length < 9) { S.mode = 0; S.selOnly = true; }   // selection only was mode 6 once (before a[8])
    if (a.length >= 10 && RANGE_MAXES.includes(a[9])) S.rangeMax = a[9];
    S.cull = a[10] > 0;
    S.edgeView = a[11] === 1 || a[11] === 2 ? a[11] : 0;
    S.cullT = a.length >= 13 && Math.abs(a[12]) < 1 ? a[12] : 0;
    if (a.length >= 14 && DENS_RS.includes(a[13])) S.densR = a[13];
    S.densK = a.length >= 15 && Math.abs(a[14]) <= 4 ? a[14] : 0;
    S.voxels = a.length < 16 || a[15] > 0;
    S.featView = [1, 2, 3].includes(a[16]) ? a[16] : 0;
  }
  if ((a = nums(h.off))) for (const i of a) if (v.clouds[i]) v.clouds[i].visible = false;
  if (h.ov !== undefined) { ovOn.clear(); for (const k of h.ov.split(',')) if (k) ovOn.add(k); }
  const m = h.m as Mode;
  if (m === 'orbit' || m === 'walk' || m === 'plan') cam.mode = m;
  return !!(h.e || h.p);
}

function defaultView() {
  const b = v.bbox;
  const c: V3 = [(b[0] + b[3]) / 2, (b[1] + b[4]) / 2, v.floorZ + 1];
  const R = Math.max(b[3] - b[0], b[4] - b[1]) / 2;
  cam.eye = [c[0] + R * 0.25, c[1] - R * 1.1, v.floorZ + R * 0.9];
  cam.lookAt(c);
  ctl.pivot = c;
  ctl.depth = dist(cam.eye, c);
  cam.pc = [c[0], c[1]];
  cam.ps = (Math.max(b[3] - b[0], b[4] - b[1]) * 1.15) / Math.max(300, Math.min(canvas.clientWidth, canvas.clientHeight));
}

/** Orbit continuously for ms and report frames per second (for testing). */
function bench(ms = 4000, low = false): Promise<{ fps: number; drawn: number; px: string }> {
  return new Promise(res => {
    const t0 = performance.now(), p = ctl.pivot ?? cam.eye;
    let n = 0;
    const step = (t: number) => {
      if (t - t0 > ms) { v.moving = false; v.dirty = true; res({ fps: (n * 1000) / (t - t0), drawn: v.drawnPts, px: `${(v.renderScale() * cam.w) | 0}x${(v.renderScale() * cam.h) | 0}` }); return; }
      if (cam.ortho) cam.zoomPlan(1.0005, cam.w / 2, cam.h / 2); else cam.orbit(p, 0.004, 0);
      v.moving = low; v.dirty = true; n++;
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

// ── main loop ─────────────────────────────────────────────────────────
let last = performance.now(), idle = 0;
function loop(now: number) {
  requestAnimationFrame(loop);
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  ctl.tick(dt);
  if (!v.scene) return;
  if (v.dirty) {
    v.render();
    v.updateLoading();
    if (measure.length) drawLabels();
    vols.updateHandles();
    hud();
    frames++;
  } else if (++idle % 10 === 0) v.updateLoading();
  if (now - tFps > 500) {
    fps = (frames * 1000) / (now - tFps); frames = 0; tFps = now;
    if (!settled && v.loadedPts && !v.inFlight && !edit.loadProgress) settled = now - t0;
    stats();
  }
}
v.onChange = () => {};
ctl.onMove = queueHash;
addEventListener('resize', () => { v.dirty = true; });
addEventListener('hashchange', () => { readHash(); edit.sync(); syncPanel(); syncModeUI(); applyOverlays(); v.dirty = true; });
document.addEventListener('visibilitychange', () => { v.dirty = true; });

async function start() {
  if (!scene && !legacyData) {
    const list = await listScenes();        // the editor: its scenes first
    if (list) { document.body.classList.add('scenes-mode'); renderSelector($('scenes'), list); editorFeedback(); return; }
  }
  void v.load(dataBase).then(onLoaded).catch(async e => {
    // a scene with nothing built yet: back to the scenes, with this one open to add scans to it
    const list = scene ? await listScenes() : null;
    if (list?.scenes.some(s => s.name === scene)) { document.body.classList.add('scenes-mode'); renderSelector($('scenes'), list, scene); editorFeedback(() => ({ scene, error: String(e?.message || e) })); return; }
    fail('Could not load the dataset: ' + (e?.message || e));
  });
}
void start();

async function onLoaded() {
  document.title = v.scene.name + ' · cloudview';
  $('title').textContent = v.scene.name;
  defaultView();
  readHash();
  initTypes();
  buildCloudList(); buildOverlayList(); syncPanel(); syncModeUI(); applyOverlays();
  await vols.load();
  align.load();
  edit.onDensity = syncOutputs;   // a new measurement: the Red at slider's density
  edit.onEditor = () => dock.registerFrom($('panes'), true);
  dock.onFront = () => edit.cons.setOn(dock.showing('construction'));   // Construction in front: every construction overlay on
  edit.showTab = showPanel;
  edit.popOut = popOut;
  edit.init().then(() => {
    // Point density and Image features are the editor's (it shows them): elsewhere a link with one opens in RGB
    if (S.mode >= 8 && $('shadetop').querySelector<HTMLElement>(`button[data-m="${S.mode}"]`)?.hidden) { S.mode = 0; syncPanel(); }
  }).catch(e => console.warn('editor:', e));
  v.dirty = true;
  requestAnimationFrame(loop);
  (window as unknown as { cv: unknown }).cv = { v, ctl, vols, align, edit, dock, bench, get settled() { return settled; } };
  showCache();
  // keep everything in this browser, in the background: the clouds, and in the editor each scan's full build
  persisted = await cache.persist();
  edit.onSuspend = () => { pf?.cancel(); };
  pf = new Prefetcher(v, async () => {
    if (!edit.editable.size) return [];
    const man = await cache.fetchFreshJson<Record<string, OrigEntry>>(dataBase + 'orig/manifest.json');
    return Object.values(man).flatMap(e => [e.index, e.bin, e.pid, ...(e.frame ? [e.frame] : [])]).map(f => dataBase + 'orig/' + f);
  });
  setTimeout(() => pf!.run().catch(e => console.warn('prefetch:', e)), 3000);   // after the first view has loaded
  if (!edit.editable.size) void publicPicker($('title'), scene ?? 'church');
}

let pf: Prefetcher | null = null, persisted = false;

/** How much this browser keeps (the cached clouds, mostly), and a button to forget it. */
async function showCache() {
  const n = await cache.usage();
  $('cachesz').textContent = n === null ? 'browser cache: unavailable' : `kept in this browser: ${(n / 1e6).toFixed(0)} MB`;
}
$('cacheclr').onclick = async () => { pf?.cancel(); pf = null; await cache.clear(); await clearFeats(); $('cachesz').textContent = 'browser cache cleared'; };   // the usage estimate lags behind

/** The background copy, in the status bar while it runs (Cancel stops it until the page is opened again). */
let pfHead: Proc | null = null;
function copyProc() {
  if (!pf || (pf.state !== 'checking' && pf.state !== 'fetching')) { pfHead?.end(); pfHead = null; return; }
  pfHead ??= proc('keeping the clouds in this browser', { cancel: () => pf?.cancel() });
  const mb = (n: number) => (n / 1e6).toFixed(0);
  if (pf.state === 'checking') pfHead.set('checking what is kept');
  else pfHead.set(`${mb(pf.have)} of ${mb(pf.total)} MB${v.inFlight || document.hidden ? ' (waiting for the view)' : ''}`, pf.total ? pf.have / pf.total : null);
}

/** The background copy's progress, for the stats. */
function copyLine() {
  if (!pf) return '';
  const mb = (n: number) => (n / 1e6).toFixed(0);
  const what = pf.state === 'off' ? `off (${pf.error})` : pf.state === 'done' ? `complete, ${mb(pf.have)} MB` : pf.state === 'checking' ? 'checking…' : `${mb(pf.have)} / ${mb(pf.total)} MB`;
  return `\nkept in this browser: ${what}${persisted ? ' (persistent)' : ''}${cache.offline.used ? ' · offline: showing the kept copy' : ''}`;
}
